/**
 * AcuityTrendsOverlay - Patient Acuity Trends Dashboard
 * Admin-only overlay for viewing patient acuity trends
 */

import { useState, useEffect, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, TrendingUp, TrendingDown, Users, AlertTriangle,
  CheckCircle, Target, RefreshCw, Loader2, ChevronRight,
  ArrowUp, ArrowDown, Minus, BarChart3, Brain
} from "lucide-react";
import api from "@/lib/api";

export default function AcuityTrendsOverlay({ isOpen, onClose, userRole }) {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [timeRange, setTimeRange] = useState("14");

  // Check admin access
  const isAdmin = userRole?.toLowerCase() === "admin" || userRole?.toLowerCase() === "administrator";

  useEffect(() => {
    if (isOpen) {
      fetchDashboard();
    }
  }, [isOpen, timeRange]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/scheduling/acuity-dashboard?days=${timeRange}`);
      setDashboard(response.data);
    } catch (err) {
      // Demo data
      setDashboard({
        summary: {
          total_patients: 156,
          high_acuity: 23,
          medium_acuity: 78,
          low_acuity: 55,
          trending_up: 12,
          trending_down: 8
        },
        patients: [
          { id: "1", name: "John Smith", acuity: "high", pps: 30, trend: "up", change: "+5", diagnosis: "Cancer" },
          { id: "2", name: "Mary Johnson", acuity: "high", pps: 35, trend: "stable", change: "0", diagnosis: "Heart Disease" },
          { id: "3", name: "Robert Williams", acuity: "medium", pps: 45, trend: "down", change: "-10", diagnosis: "COPD" },
          { id: "4", name: "Patricia Brown", acuity: "medium", pps: 50, trend: "up", change: "+5", diagnosis: "Dementia" },
          { id: "5", name: "James Davis", acuity: "low", pps: 60, trend: "stable", change: "0", diagnosis: "Renal Disease" },
          { id: "6", name: "Linda Wilson", acuity: "high", pps: 25, trend: "up", change: "+10", diagnosis: "Cancer" }
        ],
        distribution: {
          critical: 8,
          serious: 15,
          moderate: 45,
          mild: 33,
          stable: 55
        },
        alerts: [
          { type: "critical", message: "6 patients showing rapid decline", count: 6 },
          { type: "warning", message: "12 patients trending upward in acuity", count: 12 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const getAcuityColor = (acuity) => {
    switch (acuity) {
      case "high": return "bg-red-500/20 text-red-400 border-red-500/50";
      case "medium": return "bg-amber-500/20 text-amber-400 border-amber-500/50";
      case "low": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/50";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up": return <ArrowUp className="w-4 h-4 text-red-400" />;
      case "down": return <ArrowDown className="w-4 h-4 text-emerald-400" />;
      default: return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  // Access check for non-admins
  if (!isAdmin) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[500px] max-w-[95vw] bg-slate-900 border-slate-700 text-white">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-red-400" />
              Access Restricted
            </SheetTitle>
          </SheetHeader>
          <div className="mt-8 text-center">
            <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Administrator Access Required</h3>
            <p className="text-slate-400">
              Patient Acuity Trends is restricted to administrators only.
            </p>
            <Button onClick={onClose} className="mt-6">Close</Button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[900px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-hidden flex flex-col">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-violet-400" />
            Patient Acuity Trends
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            Monitor patient acuity levels and identify deteriorating patients
          </SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col mt-4">
            {/* Time Range Selector */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchDashboard} className="border-slate-600">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3 mb-4 flex-shrink-0">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-slate-400 text-xs">
                    <Users className="w-4 h-4" />
                    Total Patients
                  </div>
                  <p className="text-xl font-bold text-white mt-1">{dashboard?.summary?.total_patients}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-900/30 border-red-500/30">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-red-400 text-xs">
                    <AlertTriangle className="w-4 h-4" />
                    High Acuity
                  </div>
                  <p className="text-xl font-bold text-red-400 mt-1">{dashboard?.summary?.high_acuity}</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-900/30 border-amber-500/30">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-amber-400 text-xs">
                    <Activity className="w-4 h-4" />
                    Medium Acuity
                  </div>
                  <p className="text-xl font-bold text-amber-400 mt-1">{dashboard?.summary?.medium_acuity}</p>
                </CardContent>
              </Card>
              <Card className="bg-emerald-900/30 border-emerald-500/30">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 text-emerald-400 text-xs">
                    <CheckCircle className="w-4 h-4" />
                    Low Acuity
                  </div>
                  <p className="text-xl font-bold text-emerald-400 mt-1">{dashboard?.summary?.low_acuity}</p>
                </CardContent>
              </Card>
            </div>

            {/* Alerts */}
            {dashboard?.alerts?.length > 0 && (
              <div className="space-y-2 mb-4 flex-shrink-0">
                {dashboard.alerts.map((alert, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded ${
                    alert.type === 'critical' ? 'bg-red-900/30 text-red-300 border border-red-500/30' : 'bg-amber-900/30 text-amber-300 border border-amber-500/30'
                  }`}>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {alert.message}
                    </div>
                    <Badge variant="outline" className={alert.type === 'critical' ? 'border-red-500/50 text-red-400' : 'border-amber-500/50 text-amber-400'}>
                      {alert.count} patients
                    </Badge>
                  </div>
                ))}
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="bg-slate-800 flex-shrink-0">
                <TabsTrigger value="overview" className="data-[state=active]:bg-violet-600">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="patients" className="data-[state=active]:bg-violet-600">
                  <Users className="w-4 h-4 mr-2" />
                  Patients
                </TabsTrigger>
                <TabsTrigger value="distribution" className="data-[state=active]:bg-violet-600">
                  <Target className="w-4 h-4 mr-2" />
                  Distribution
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1 mt-4">
                <TabsContent value="overview" className="mt-0 space-y-4">
                  {/* Trend Summary */}
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-lg text-white">Acuity Trends</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-red-400" />
                            <span className="text-slate-300">Trending Up (Deteriorating)</span>
                          </div>
                          <p className="text-3xl font-bold text-red-400 mt-2">{dashboard?.summary?.trending_up}</p>
                          <p className="text-xs text-slate-500 mt-1">patients in last {timeRange} days</p>
                        </div>
                        <div className="p-4 bg-emerald-900/20 border border-emerald-500/30 rounded-lg">
                          <div className="flex items-center gap-2">
                            <TrendingDown className="w-5 h-5 text-emerald-400" />
                            <span className="text-slate-300">Trending Down (Improving)</span>
                          </div>
                          <p className="text-3xl font-bold text-emerald-400 mt-2">{dashboard?.summary?.trending_down}</p>
                          <p className="text-xs text-slate-500 mt-1">patients in last {timeRange} days</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="patients" className="mt-0 space-y-3">
                  {dashboard?.patients?.map(patient => (
                    <Card key={patient.id} className="bg-slate-800/50 border-slate-700">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-white">{patient.name}</span>
                              <Badge variant="outline" className={getAcuityColor(patient.acuity)}>
                                {patient.acuity}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-400">{patient.diagnosis}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-slate-400">PPS Score</p>
                              <p className="text-lg font-bold text-white">{patient.pps}%</p>
                            </div>
                            <div className="flex items-center gap-1">
                              {getTrendIcon(patient.trend)}
                              <span className={`text-sm font-medium ${
                                patient.trend === 'up' ? 'text-red-400' : 
                                patient.trend === 'down' ? 'text-emerald-400' : 'text-slate-400'
                              }`}>
                                {patient.change}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="distribution" className="mt-0">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-lg text-white">Patient Distribution</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {Object.entries(dashboard?.distribution || {}).map(([level, count]) => (
                        <div key={level} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-300 capitalize">{level}</span>
                            <span className="text-white font-medium">{count} patients</span>
                          </div>
                          <Progress 
                            value={(count / dashboard?.summary?.total_patients) * 100} 
                            className={`h-2 ${
                              level === 'critical' ? '[&>div]:bg-red-500' :
                              level === 'serious' ? '[&>div]:bg-orange-500' :
                              level === 'moderate' ? '[&>div]:bg-amber-500' :
                              level === 'mild' ? '[&>div]:bg-blue-500' :
                              '[&>div]:bg-emerald-500'
                            }`}
                          />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-4 flex-shrink-0 pt-4 border-t border-slate-700">
              <Button variant="outline" onClick={fetchDashboard} className="border-slate-600">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button className="flex-1 bg-violet-600 hover:bg-violet-700">
                <Brain className="w-4 h-4 mr-2" />
                Run AI Analysis
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export { AcuityTrendsOverlay };
